import axios from 'axios'

const api = axios.create({
  baseURL: '/api/v1',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use(config => {
  const token = localStorage.getItem('tonys-admin-token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  res => res.data,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('tonys-admin-token')
      window.dispatchEvent(new Event('admin:logout'))
    }
    const msg = err.response?.data?.detail || err.message || 'Request failed'
    return Promise.reject(new Error(typeof msg === 'string' ? msg : JSON.stringify(msg)))
  }
)

// ── Auth ──────────────────────────────────────────────────────────
export const authService = {
  login: (username, password) =>
    api.post('/login', new URLSearchParams({ username, password }), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }),
  login2fa: (username, password, totp_code) =>
    api.post('/login/2fa', { username, password, totp_code }),
  me: () => api.get('/me'),
  changePassword: (current_password, new_password) =>
    api.post('/me/change-password', { current_password, new_password }),
}

// ── Dashboard ─────────────────────────────────────────────────────
export const dashboardService = {
  getStats: () => api.get('/admin/stats'),
  getRecentOrders: () => api.get('/orders/', { params: { limit: 10 } }),
  getLowStock: () => api.get('/stock/', { params: { limit: 8 } }),
  getRevenueData: (period = '7d') => api.get(`/admin/revenue?period=${period}`),
  getTrend: (months = 7) => api.get(`/admin/trend?months=${months}`),
}

// ── Products ──────────────────────────────────────────────────────
export const productService = {
  getAll: (params = {}) => api.get('/products/priced', { params }),
  getAdminList: (params = {}) => api.get('/admin/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  getBrands: () => api.get('/products/brands'),
  backfillBrands: (apply = false, only_empty = true) =>
    api.post('/admin/products/backfill-brands', null, { params: { apply, only_empty } }),
  backfillCategories: (apply = false, only_empty = true) =>
    api.post('/admin/products/backfill-categories', null, { params: { apply, only_empty } }),
  // Scraped-image catalogue search (best-to-worst ranked thumbnails)
  imageLookup: (params = {}) => api.get('/admin/image-lookup', { params }),
  applyScrapedImages: (id, rel_paths, as_primary = true) =>
    api.post(`/products/${id}/apply-scraped-image`, { rel_paths, as_primary }),
  getTopProducts: (limit = 100) => api.get('/admin/reports/top-products', { params: { limit } }),
  create: (data) => api.post('/products/', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  saveDetails: (id, data) => api.patch(`/products/${id}/details`, data),
  delete: (id) => api.delete(`/products/${id}`),
  updateStock: (id, qty) => api.put(`/stock/${id}`, { quantity: qty }),
  uploadImage: (id, file) => {
    const form = new FormData()
    form.append('file', file)
    return api.post(`/products/${id}/upload-image`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  uploadAdditionalImage: (id, file) => {
    const form = new FormData()
    form.append('file', file)
    return api.post(`/products/${id}/upload-additional-image`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  getImages: (id) => api.get(`/products/${id}/images`),
  importCsv: (file) => {
    const form = new FormData()
    form.append('file', file)
    return api.post('/upload_products_csv', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    })
  },
}

// ── Categories ────────────────────────────────────────────────────
export const categoryService = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
}

// ── Orders ────────────────────────────────────────────────────────
export const orderService = {
  getAll: (params = {}) => api.get('/orders/', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  getDetail: (id) => api.get(`/orders/${id}/detail`),
  getCsvUrl: (id) => `${api.defaults.baseURL}/orders/${id}/csv`,
  // Authenticated download of the POS import file (needs the Bearer token, so a plain
  // <a href> won't work). Returns the file as a Blob.
  downloadPosCsv: (id) => api.get(`/orders/${id}/csv`, { responseType: 'blob' }),
  updateStatus: (id, status) => api.post(`/orders/${id}/status`, { status }),
  bulkUpdateStatus: (order_ids, status) => api.post('/orders/bulk-status', { order_ids, status }),
}

// ── Customers ─────────────────────────────────────────────────────
export const customerService = {
  getAll: (params = {}) => api.get('/customers/', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  update: (id, data) => api.patch(`/customers/${id}`, data),
}

// ── Customer invitations (onboarding) ──────────────────────────────
export const inviteService = {
  create: (data) => api.post('/invites/create', data),          // {customer_id, channel}
  send: (inviteId, data = {}) => api.post(`/invites/${inviteId}/send`, data),
  list: (params = {}) => api.get('/invites', { params }),
}

// ── Users (admin) ──────────────────────────────────────────────────
export const userService = {
  getAll: () => api.get('/admin/users'),
  reset2fa: (userId) => api.delete(`/admin/users/${userId}/2fa`),
}

// ── Reports ───────────────────────────────────────────────────────
export const reportService = {
  getSalesSummary: (from, to) => api.get('/admin/reports/sales', { params: { from_date: from, to_date: to } }),
  getCustomerOrders: (from, to) => api.get('/admin/reports/customer-orders', { params: { from_date: from, to_date: to } }),
  getPromotionPerformance: () => api.get('/admin/promotions/performance'),
  getTopProducts: (limit = 10) => api.get('/admin/reports/top-products', { params: { limit } }),
  getCategoryBreakdown: () => api.get('/admin/reports/categories'),
}

// ── Forecasting ───────────────────────────────────────────────────
export const forecastService = {
  get: (params) => api.get('/admin/forecast', { params }),
  getProduct: (params) => api.get('/admin/forecast/product', { params }),
  getTopProducts: (limit = 20) => api.get('/admin/forecast/top-products', { params: { limit } }),
  // Editable demand calendar (SA holidays / liquor events / voting-day bans)
  listEvents: (params) => api.get('/admin/forecast/events', { params }),
  createEvent: (data) => api.post('/admin/forecast/events', data),
  updateEvent: (id, data) => api.patch(`/admin/forecast/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/admin/forecast/events/${id}`),
  resetEvents: () => api.post('/admin/forecast/events/reset-defaults'),
}

// ── Promotions ────────────────────────────────────────────────────
export const promotionService = {
  getAll: () => api.get('/admin/promotions'),
  create: (data) => api.post('/admin/promotions', data),
  update: (id, data) => api.put(`/admin/promotions/${id}`, data),
  remove: (id) => api.delete(`/admin/promotions/${id}`),
  push: (id, customer_ids = null) => api.post(`/admin/promotions/${id}/push`, customer_ids ? { customer_ids } : {}),
  getTargets: (id) => api.get(`/admin/promotions/${id}/targets`),
}

// ── Sales history import (POS flat file → forecast training data) ──
export const salesHistoryService = {
  inspect: (file) => {
    const form = new FormData()
    form.append('file', file)
    return api.post('/admin/sales-history/inspect', form, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
    })
  },
  import: (file, mapping, dateFormat = 'dmy') => {
    const form = new FormData()
    form.append('file', file)
    form.append('mapping', JSON.stringify(mapping))
    form.append('date_format', dateFormat)
    return api.post('/admin/sales-history/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
    })
  },
  batches: () => api.get('/admin/sales-history/batches'),
  deleteBatch: (id) => api.delete(`/admin/sales-history/batch/${id}`),
}

// ── System Settings ───────────────────────────────────────────────
export const settingsService = {
  get: () => api.get('/admin/settings'),
  update: (data) => api.patch('/admin/settings', data),
}

// ── Single-unit restricted categories ────────────────────────────
export const restrictedCategoriesService = {
  getAll: () => api.get('/admin/single-unit-restricted'),
  add: (name) => api.post('/admin/single-unit-restricted', { name }),
  remove: (id) => api.delete(`/admin/single-unit-restricted/${id}`),
}

export default api

// ── Scheduler ─────────────────────────────────────────────────────
export const schedulerService = {
  getJobTypes:   ()         => api.get('/admin/scheduler/job-types'),
  getJobs:       ()         => api.get('/admin/scheduler/jobs'),
  getJob:        (id)       => api.get(`/admin/scheduler/jobs/${id}`),
  createJob:     (data)     => api.post('/admin/scheduler/jobs', data),
  updateJob:     (id, data) => api.put(`/admin/scheduler/jobs/${id}`, data),
  deleteJob:     (id)       => api.delete(`/admin/scheduler/jobs/${id}`),
  runNow:        (id)       => api.post(`/admin/scheduler/jobs/${id}/run`),
  getRuns:       (id)       => api.get(`/admin/scheduler/jobs/${id}/runs`),
  getOutputs:    ()         => api.get('/admin/scheduler/outputs'),
  getStatus:     ()         => api.get('/admin/scheduler/status'),
  downloadUrl:   (filename) => `/admin/scheduler/outputs/${filename}`,
}
